import React, {Component, ReactNode} from 'react';
import Draggable, {DraggableData, DraggableEvent} from 'react-draggable';
import PokerMenuContainer from 'app/components/poker/poker_menu_container/PokerMenuContainer';
import PokerMoveButtonDraggable from 'app/components/poker/poker_move_buttons/PokerMoveButtonDraggable';
import PokerMoveButtonMinimize from 'app/components/poker/poker_move_buttons/PokerMoveButtonMinimize';
import PokerRoom from 'app/components/poker/poker_room/PokerRoom';
import PokerPlayersAmount from 'app/components/poker/poker_players_amount/PokerPlayersAmount';
import {inject, observer, Provider} from 'mobx-react';
import {PokerStore} from 'app/store/PokerStore';
import {PokerController} from 'app/controllers/PokerController';
import PokerMinimize from 'app/components/poker/poker_minimize/PokerMinimize';
import {getPositionX} from 'app/utils/draggable/getPositionX';
import {getPositionY} from 'app/utils/draggable/getPositionY';
import {defaultPositionX} from 'app/utils/draggable/defaultPositionX';
import {defaultPositionY} from 'app/utils/draggable/defaultPositionY';
import {
    POKER_CONTAINER_POSITION_X_KEY,
    POKER_CONTAINER_POSITION_Y_KEY
} from 'app/utils/Consts';
import {windowWidth} from 'app/utils/draggable/windowWidth';
import {windowHeight} from 'app/utils/draggable/windowHeight';
import {isDesktop, isDesktopSmall} from 'app/utils';
import {TableModel} from 'app/store/poker/TableModel';

interface Props {
    pokerStore?: PokerStore
    table: TableModel
    onActivate: (id: number) => void
    lastDraggedElement: boolean
}

interface State {
    height: number
    positionX: number
    positionY: number
    scale: number
    reloaderKey: string
}

const PIXELS_LIMIT = 95;
const POKER_CONTAINER_HEIGHT = 624;
const ExampleNUMBER = 892.5;

@inject('pokerStore')
@observer
export default class PokerContainer extends Component<Props, State> {
    controller: PokerController;

    table: TableModel;

    constructor(props: Props) {
        super(props);
        this.controller = new PokerController(this.props.pokerStore, props.table);
        this.table = props.table;
        this.table.setController(this.controller);
        this.state = {
            height: 0,
            positionX: getPositionX(POKER_CONTAINER_POSITION_X_KEY),
            positionY: getPositionY(POKER_CONTAINER_POSITION_Y_KEY, POKER_CONTAINER_HEIGHT),
            reloaderKey: null,
            scale: this.scaleValueTransform()
        };
    }

    scaleValueTransform = (): number => window.innerHeight / ExampleNUMBER;

    handleResize = (): void => {
        this.setState({reloaderKey: Math.random().toString(), scale: this.scaleValueTransform()});
    };

    componentDidMount(): void {
        window.addEventListener('resize', this.handleResize);
        this.handleResize();
    }

    componentWillUnmount(): void {
        window.removeEventListener('resize', this.handleResize);
    }

    dropOut = (): void => {
        const {table} = this;

        if (table.player && !table.currentTable.is_shutdown) {
            this.controller.dropOut(table.currentTable.table_id);
        }
    };

    sitOut = (): void => {
        this.controller.sitOut();
    };

    sitIn = (): void => {
        const {table} = this;

        if (Number(table.player.chips) === 0) {
            table.buyIn();
            return;
        }

        this.controller.sitIn(table.currentTable.table_id, 0, 0, null);
    };

    minimize = (): void => {
        this.table.minimizeTable();

        if (!isDesktopSmall()) {
            document.body.classList.remove('body-overflow');
        }
    };

    closeTable = (): void => {
        this.table.closeTable();

        if (!isDesktopSmall()) {
            document.body.classList.remove('body-overflow');
        }
    };

    handleStop = (e: DraggableEvent, data: DraggableData): void => {
        localStorage.setItem(POKER_CONTAINER_POSITION_X_KEY, (data.x || defaultPositionX()).toString());
        localStorage.setItem(POKER_CONTAINER_POSITION_Y_KEY, ((data.y || defaultPositionY(POKER_CONTAINER_HEIGHT))).toString());
        this.props.onActivate(this.table.currentTable.id);
    };

    render(): ReactNode {
        const {table, lastDraggedElement} = this.props;

        if (!table.currentTable) {
            return <></>;
        }

        if (table.isMinimize) {
            return <Provider controller={this.controller}>
                <PokerMinimize
                    onMininimize={this.minimize}
                    player={table.player}
                    countPlayers={table.currentTable.count_players}
                    small_blind={table.currentTable.small_blind}
                    big_blind={table.currentTable.big_blind}
                />
            </Provider>;
        }

        const {count_players} = table.currentTable;

        const {positionX, positionY} = this.state;
        const desktopDevice = isDesktop();
        const desktopDeviceSmall = isDesktopSmall();

        return <Provider controller={this.controller}>
            <Draggable
                defaultPosition={{x: Number(positionX), y: Number(positionY)}}
                handle={desktopDeviceSmall ? '.poker-move-button' : 'strongMobile'}
                onStop={this.handleStop}
                bounds={{bottom: windowHeight() - PIXELS_LIMIT, left: 0, right: windowWidth() - POKER_CONTAINER_HEIGHT - PIXELS_LIMIT, top: 0}}
            >
                <div className={`poker-draggable${lastDraggedElement ? ' react-last-dragged' : ''}`}>
                    <div className="poker">
                        <div className="poker__header">
                            <PokerMenuContainer
                                closeTable={this.dropOut}
                                sitOut={this.sitOut}
                                sitIn={this.sitIn}
                                player={table.player}
                                isEnabledSound={table.isEnabledSound}
                                enableSound={table.toggleSound}
                                tableId={table.currentTable.table_id}
                                playmoney={table.currentTable.playmoney}
                                key={this.state.reloaderKey}
                            />
                            <div className="poker__header-right">
                                {desktopDevice && <PokerPlayersAmount currentCount={count_players} />}
                                <PokerMoveButtonDraggable/>
                                <PokerMoveButtonMinimize onClick={this.minimize}/>
                                <button className="poker-move-button close-poker" onClick={this.closeTable}>
                                    <svg className="svg-default">
                                        <use xlinkHref="#svg-bet-slip-controller-close"/>
                                    </svg>
                                </button>
                            </div>
                        </div>
                        <div className="poker__body">
                            {desktopDeviceSmall && <span className="poker-table-name">{this.table.name}</span>}
                            <PokerRoom
                                table={this.table}
                                scaleTransform={this.state.scale}
                                reloaderKey={this.state.reloaderKey}
                            />
                        </div>
                    </div>
                </div>
            </Draggable>
        </Provider>;
    }
}
